/**
 * Blocked tab.
 * Wave 1: reason Pareto chips + Project / Discipline / Owner filters + search, the "Reason missing"
 * callout with "Set reasons" for a group of drawings at once, no repeated "Blocked" badge, no "NONE".
 * Wave 2:
 *  - #1 grouped by project: "ATS Majlis SAMHA #62 · LCS BMS · since 6 Jul 2026 [15]" and "86 d";
 *  - #2 a compact triage table: Drawing | Disc. | Reason | Action note | Owner | Blocked for | actions;
 *  - #5 in-row actions: the Reason opens "Set blocker reason" (6 reasons, a note, the owner, "apply to
 *    all 23 in Villa 3"), "Unblock ▾" moves a drawing on (In progress / Submitted / Approved …), the
 *    group check box + the bar unblock or set a reason for a whole project at once;
 *  - #6 "Blocked for" from the status history (last change to Blocked, else when it was added),
 *    coloured by age, longest blocked first.
 * Wave 3:
 *  - #7 Action note vs status: "CODE C" → Suggest reason: Consultant code C · Accept; "Submitted via email" →
 *    move to Submitted?; "APPROVED" → close as Approved?; the callout counts them and "Review N mismatches" accepts
 *    whole groups (also approved drawings that still carry a blocker). New reason "Consultant comments (code C)".
 *  - #9 List | Board (a column per reason, drag a card to set the reason or to unblock it), the Sunday digest to
 *    owners (DigestPanel.js) and "Request info…" — a ready email for the consultant / site, logged in the history.
 * `readOnly` (BUG-002: Shop Drawings is View only for the role, or a Viewer account): the list, the board and the
 * digest preview only — plain Reason badges, no Unblock, no Set reasons / Review mismatches / Request info, no check
 * boxes or bulk bar, a board without drag; ⋯ keeps the page's read items.
 */
import { useCallback, useMemo, useState } from 'react';
import { ArrowUpDown, ChevronDown, CircleAlert, Download, Kanban, List, Loader2, Mail, SearchX, Unlock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EmptyState } from '@/components/ui/empty-state';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { notify } from '@/components/ui/notify';
import { RowActions } from '@/components/ui/row-actions';
import { getErrorMessage } from '@/lib/errors';
import { formatNumber } from '@/lib/format';
import { getStatusTone } from '@/lib/status';
import { cn } from '@/lib/utils';
import {
  BLOCKER_HINTS,
  BLOCKER_LABELS,
  BLOCKER_REASONS,
  NEW_BLOCKERS,
  STATUS_LABELS,
  UNBLOCK_STATUSES,
  blockedFallbackNote,
  blockedGroupSort,
  blockedReasonCounts,
  blockedSince,
  blockedSinceText,
  blockedSortValues,
  blockerMeta,
  defaultOpenOnPage,
  disciplinesOf,
  displayPerson,
  drawingTitle,
  isBlocked,
  isEmptyNote,
  labelize,
  mismatchGroups,
  mostCommon,
  optionCounts,
  orderedGroups,
  projectNumber,
  requestInfoEmail,
  siblingsOf,
  sortWithColumns,
  valueKey,
} from './model';
import { BlockedBoard } from './BlockedBoard';
import { DigestButton, DigestPanel, useDigest } from './DigestPanel';
import { EmailComposer } from './EmailComposer';
import { MismatchDialog, SuggestionTag } from './MismatchReview';
import {
  AgeCell,
  AgeText,
  BulkBar,
  BulkButton,
  Callout,
  ChipSelect,
  CountPill,
  DisciplineChip,
  DrawingIdText,
  GroupTitle,
  PersonAvatar,
  PersonChip,
  ReasonBadge,
  SdCheckbox,
  SearchField,
  TableFoot,
  ViewSwitch,
} from './SdUi';

const fmt = (value) => formatNumber(value, { maxDecimals: 0 });
const projectKeyOf = (drawing) => valueKey(drawing.project) || '__none__';
const titleKey = (drawing) => `${projectKeyOf(drawing)}|${valueKey(drawingTitle(drawing))}`;
// 14 #1: the first projects open with a few rows each + "more in <project>", so all of them scan on one screen.
const GROUP_ROW_LIMIT = 3;
const DEFAULT_OPEN_ROWS = 9;
const MENU_ITEM = 'h-[34px] gap-2.5 rounded-lg px-2.5 text-[13px] text-ink focus:bg-surface-hover focus:text-ink';
const SORTS = [
  { value: 'since:desc', label: 'Longest blocked' },
  { value: 'since:asc', label: 'Most recently blocked' },
  { value: 'drawing:asc', label: 'Drawing A–Z' },
  { value: 'owner:asc', label: 'Owner A–Z' },
];

function Dash() {
  // `relative` keeps the absolutely positioned sr-only text inside the cell (on a phone the table scrolls sideways).
  return (
    <span className="relative">
      <span className="text-ink-4" aria-hidden="true">—</span>
      <span className="sr-only">None</span>
    </span>
  );
}

/* ------------------------------------------------------------------ cells */

/** Title of a blocked drawing; when several in the project share it (Villa 3's 23 rooms) the floor / area is added. */
function blockedTitle(drawing, ambiguous) {
  const title = drawingTitle(drawing);
  const floor = String(drawing.floor || '').trim();
  return ambiguous && floor && floor !== '-' && floor !== title ? { title: `${title} · ${floor}`, usedFloor: true } : { title, usedFloor: false };
}

function BlockedDrawingCell({ drawing, ambiguous }) {
  const { title, usedFloor } = blockedTitle(drawing, ambiguous);
  const place = [drawing.block, usedFloor ? '' : drawing.floor].map((value) => String(value || '').trim()).filter((value) => value && value !== '-' && value !== title);
  // "MAIN · Podium -2 · BMS-016" (14 mockup): the internal SD number lives in the tooltip and the drawer; the
  // block / floor gives way before a real Drawing ID, which keeps up to ~140px.
  return (
    <div className="min-w-0 py-0.5">
      <span className="block truncate text-[13px] font-semibold text-ink" title={`${title} · ${drawing.id}`}>{title}</span>
      <span className="mt-0.5 flex min-w-0 items-center gap-1.5 whitespace-nowrap text-[11.5px] text-ink-3">
        {place.length ? <><span className="min-w-0 shrink truncate" title={place.join(' · ')}>{place.join(' · ')}</span><span className="shrink-0" aria-hidden="true">·</span></> : null}
        <DrawingIdText value={drawing.drawingId} keepEnd className="min-w-0 max-w-[140px] shrink-0" />
      </span>
    </div>
  );
}

function ActionNoteCell({ drawing, onApplySuggestion }) {
  const action = isEmptyNote(drawing.action) ? '' : String(drawing.action).trim();
  if (!action) return <Dash />;
  return (
    <div className="min-w-0">
      <span className="block truncate text-[13px] font-semibold text-ink" title={action}>{action}</span>
      {onApplySuggestion ? <SuggestionTag drawing={drawing} onApply={onApplySuggestion} /> : null}
    </div>
  );
}

const BLOCKED_VIEWS = [
  { key: 'list', label: 'List', icon: List },
  { key: 'board', label: 'Board', icon: Kanban },
];

/** The Reason badge is a button: "Set blocker reason" with a note, the owner and "apply to all". */
function ReasonPopover({ drawing, drawings, people, onSaveReason }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [owner, setOwner] = useState('');
  const [ownerAtOpen, setOwnerAtOpen] = useState('');
  const [applyAll, setApplyAll] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const siblings = useMemo(() => (open ? siblingsOf(drawing, drawings) : []), [open, drawing, drawings]);
  const meta = blockerMeta(drawing);

  const onOpenChange = (next) => {
    if (saving) return;
    setOpen(next);
    if (next) {
      const current = String(drawing.resp || '').trim();
      setReason(meta.missing ? '' : String(drawing.blocker || '').toUpperCase());
      setNote('');
      setOwner(current);
      setOwnerAtOpen(current);
      setApplyAll(true);
      setError('');
    }
  };

  const save = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!reason) return;
    const targets = applyAll && siblings.length > 1 ? siblings : [drawing];
    // The Owner starts as this drawing's owner; it is sent only when the person picked another one,
    // so saving a reason never quietly re-assigns (or un-assigns) the other drawings of the group.
    const ownerChanged = owner.trim() !== ownerAtOpen;
    setSaving(true);
    setError('');
    try {
      await onSaveReason({ ids: targets.map((item) => item.id), blocker: reason, note: note.trim(), owner: ownerChanged ? owner.trim() : undefined });
      setOpen(false);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save the reason'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="-ml-1 max-w-full rounded-md p-0.5 transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-surface-hover"
          aria-label={`Reason: ${meta.label || 'none'}. Set blocker reason`}
          data-testid={`reason-button-${drawing.id}`}
        >
          <ReasonBadge drawing={drawing} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-[400px] max-w-[calc(100vw-24px)] rounded-xl border-line bg-surface p-3.5 text-ink shadow-overlay"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <form onSubmit={save} className="space-y-2.5" data-testid="reason-form">
          <div>
            <p className="text-[13px] font-bold text-ink">Set blocker reason</p>
            <p className="mt-0.5 truncate text-[11.5px] text-ink-3">{[drawingTitle(drawing), drawing.floor].filter(Boolean).join(' · ')}</p>
          </div>
          <div className="grid grid-cols-2 gap-1" role="radiogroup" aria-label="Blocker reason">
            {BLOCKER_REASONS.map((value) => (
              <label
                key={value}
                className={cn(
                  'flex h-8 cursor-pointer items-center gap-2 rounded-lg px-2 text-[12.5px] transition-colors',
                  NEW_BLOCKERS.has(value) && 'col-span-2',
                  reason === value ? 'bg-brand-soft font-semibold text-ink' : 'text-ink hover:bg-surface-hover'
                )}
              >
                <input
                  type="radio"
                  name={`reason-${drawing.id}`}
                  value={value}
                  checked={reason === value}
                  onChange={() => setReason(value)}
                  className="h-3.5 w-3.5 shrink-0 accent-[color:var(--brand)]"
                  data-testid={`row-reason-${value}`}
                />
                <span className="truncate">{labelize(value, BLOCKER_LABELS)}{BLOCKER_HINTS[value] ? <span className="font-normal text-ink-3"> ({BLOCKER_HINTS[value]})</span> : null}</span>
                {NEW_BLOCKERS.has(value) ? <span className="ml-auto shrink-0 rounded-[4px] bg-brand-soft px-1 text-[10px] font-bold text-[color:var(--brand-ink)]">new</span> : null}
              </label>
            ))}
          </div>
          <div className="h-px bg-line-2" />
          <label className="block">
            <span className="sr-only">What exactly is missing?</span>
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="What exactly is missing? e.g. room layouts from the architect"
              maxLength={500}
              className="h-14 w-full resize-none rounded-control border border-line bg-surface px-2.5 py-2 text-[12.5px] text-ink outline-none placeholder:text-ink-4 focus:border-[color:var(--brand-ink)] focus:ring-2 focus:ring-[color:var(--brand-soft-border)]"
              data-testid="reason-note"
            />
          </label>
          {siblings.length > 1 ? (
            <label className="flex cursor-pointer items-center gap-2 text-[12.5px] text-ink">
              <SdCheckbox checked={applyAll} onCheckedChange={(checked) => setApplyAll(Boolean(checked))} data-testid="reason-apply-all" aria-label={`Apply to all ${siblings.length} blocked in ${drawingTitle(drawing)}`} />
              Apply to all {fmt(siblings.length)} blocked in {drawingTitle(drawing)}
            </label>
          ) : null}
          {error ? <p className="text-[12px] font-semibold text-status-red-ink" role="alert">{error}</p> : null}
          <div className="flex items-center justify-between gap-2">
            <label className="flex min-w-0 items-center gap-1.5 text-[12px] text-ink-3">
              Owner
              <span className="relative inline-flex min-w-0 items-center">
                {owner ? <PersonAvatar name={displayPerson(owner)} size={20} className="pointer-events-none absolute left-1.5" /> : null}
                <select
                  value={owner}
                  onChange={(event) => setOwner(event.target.value)}
                  className={cn('h-8 min-w-0 max-w-[140px] appearance-none truncate rounded-full border border-line bg-surface pr-6 text-[12.5px] font-semibold text-ink outline-none focus:ring-2 focus:ring-ring', owner ? 'pl-8' : 'pl-2.5')}
                  data-testid="reason-owner"
                  aria-label="Owner"
                >
                  <option value="">Unassigned</option>
                  {[...new Set([...people, owner].filter(Boolean))].map((name) => <option key={name} value={name}>{displayPerson(name)}</option>)}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 h-3 w-3 text-ink-3" aria-hidden="true" />
              </span>
            </label>
            <Button type="submit" size="sm" disabled={!reason || saving} data-testid="save-reason">
              {saving ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
              {saving ? 'Saving…' : 'Save reason'}
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}

/** The toast every failed unblock shows (one row or the selection). */
const unblockFailed = (err) => notify.error('Could not change the status', { description: getErrorMessage(err) });

/** "Unblock ▾" of one row: saves right away, spins while saving, says so when it fails. */
function UnblockMenu({ onPick, label = 'Unblock', size = 'xs', variant = 'outline', className, testId, align = 'end' }) {
  const [busy, setBusy] = useState(false);
  const pick = async (status) => {
    setBusy(true);
    try {
      await onPick(status);
    } catch (err) {
      unblockFailed(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant={variant} size={size} className={className} data-testid={testId} disabled={busy} aria-busy={busy || undefined}>
          {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
          {label} <ChevronDown className="!size-3 opacity-70" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="min-w-[200px] rounded-xl border-line bg-surface p-1.5 text-ink shadow-overlay">
        <DropdownMenuLabel className="px-2.5 pb-1 pt-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Move to</DropdownMenuLabel>
        {UNBLOCK_STATUSES.map((status) => (
          <DropdownMenuItem key={status} className={MENU_ITEM} onSelect={() => pick(status)} data-testid={`unblock-to-${status}`}>
            <span className={cn('ui-tone-dot', `tone-${getStatusTone(status, 'shop_drawing')}`)} style={{ width: 8, height: 8 }} aria-hidden="true" />
            {labelize(status, STATUS_LABELS)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* ------------------------------------------------------------------ tab */

export function BlockedTab({
  drawings,
  rows,
  filters,
  onFiltersChange,
  loading,
  onOpen,
  onSetReasons,
  onSaveReason,
  onSetStatus,
  onBulkStatus,
  rowMenuItems,
  people = [],
  onExport,
  view = 'list',
  onViewChange,
  onApplyChanges,
  onRequestInfo,
  currentUserName = '',
  sortValue: sortProp,
  onSortValueChange,
  readOnly = false,
}) {
  // Read-only: the page passes no write callbacks either; dropping them here too keeps every write control out.
  const canApply = !readOnly && Boolean(onApplyChanges);
  const canRequest = !readOnly && Boolean(onRequestInfo);
  const [reasonsOpen, setReasonsOpen] = useState(false);
  const [reasonsPreselect, setReasonsPreselect] = useState(null);
  const [localSort, setLocalSort] = useState('since:desc');
  // The page holds the sort when it exports this view (14 #8: the file comes in the order of the screen).
  const sortValue = sortProp || localSort;
  const setSortValue = onSortValueChange || setLocalSort;
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [mismatchOpen, setMismatchOpen] = useState(false);
  const [digestOpen, setDigestOpen] = useState(false);
  const [requestFor, setRequestFor] = useState(null);
  const digest = useDigest(!loading);
  const board = view === 'board';
  const blocked = useMemo(() => drawings.filter(isBlocked), [drawings]);
  // 14 #7: action notes that disagree with the status, over the whole register (the "stale" kind is not blocked).
  const mismatches = useMemo(() => mismatchGroups(drawings), [drawings]);
  const notReallyBlocked = mismatches.approved.length + mismatches.submitted.length;
  const mismatchTotal = notReallyBlocked + mismatches.stale.length + mismatches.code_c.length;
  const applySuggestion = useMemo(() => (canApply
    ? (changes, found) => onApplyChanges(changes, { purpose: 'suggestions', message: found ? `${found.short}: “${drawingTitle(drawings.find((item) => item.id === changes[0].id) || {})}”` : undefined })
    : null), [canApply, drawings, onApplyChanges]);
  const requestInfo = useCallback((list) => setRequestFor(list), []);
  const menuFor = useCallback((drawing) => [
    canRequest ? { label: 'Request info…', icon: Mail, onSelect: () => requestInfo([drawing]), testId: `request-info-${drawing.id}` } : null,
    ...rowMenuItems(drawing),
  ].filter(Boolean), [canRequest, requestInfo, rowMenuItems]);
  const reasons = useMemo(() => blockedReasonCounts(blocked), [blocked]);
  const missing = reasons.find((reason) => reason.key === 'REASON_MISSING')?.count || 0;
  const options = useMemo(() => ({
    project: optionCounts(blocked, (drawing) => drawing.project),
    disc: optionCounts(blocked, (drawing) => drawing.disc),
    owner: optionCounts(blocked, (drawing) => drawing.resp, { emptyLabel: 'No owner', labelOf: displayPerson }).map((option) => (
      option.value === '__empty__' ? { ...option, value: '__none__' } : option
    )),
  }), [blocked]);
  const anyFilter = Boolean(filters.reason || filters.project || filters.disc || filters.owner || filters.search);
  const set = (patch) => onFiltersChange({ ...filters, ...patch });

  // Blocked for: computed once per drawing list.
  const since = useMemo(() => new Map(rows.map((drawing) => [drawing.id, blockedSince(drawing)])), [rows]);
  const daysOf = useCallback((drawing) => since.get(drawing.id)?.days ?? null, [since]);
  // Titles shared by several blocked drawings of one project: those rows also show their floor / area.
  const ambiguousTitles = useMemo(() => {
    const counts = new Map();
    blocked.forEach((drawing) => counts.set(titleKey(drawing), (counts.get(titleKey(drawing)) || 0) + 1));
    return new Set([...counts.entries()].filter(([, count]) => count > 1).map(([key]) => key));
  }, [blocked]);
  const projects = useMemo(() => {
    const map = new Map();
    blocked.forEach((drawing) => {
      const key = projectKeyOf(drawing);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(drawing);
    });
    return new Map([...map.entries()].map(([key, list]) => [key, {
      label: mostCommon(list, (drawing) => drawing.project) || 'No project',
      number: mostCommon(list, projectNumber),
    }]));
  }, [blocked]);

  const sortValues = useMemo(() => blockedSortValues(daysOf), [daysOf]);
  const columns = useMemo(() => [
    { key: 'drawing', header: 'Drawing', hideable: false, sortable: true, truncate: true, minWidth: 220, sortValue: sortValues.drawing, render: (drawing) => <BlockedDrawingCell drawing={drawing} ambiguous={ambiguousTitles.has(titleKey(drawing))} /> },
    { key: 'disc', header: 'Disc.', title: 'Discipline', width: 64, render: (drawing) => <DisciplineChip disc={drawing.disc} /> },
    {
      key: 'reason', header: 'Reason', width: 172, sortable: true, sortValue: sortValues.reason,
      render: (drawing) => (readOnly ? (
        <span className="-ml-1 inline-flex max-w-full p-0.5" data-testid={`reason-badge-${drawing.id}`}><ReasonBadge drawing={drawing} /></span>
      ) : <ReasonPopover drawing={drawing} drawings={drawings} people={people} onSaveReason={onSaveReason} />),
    },
    { key: 'action', header: 'Action note', width: 230, truncate: true, render: (drawing) => <ActionNoteCell drawing={drawing} onApplySuggestion={applySuggestion} /> },
    { key: 'owner', header: 'Owner', width: 120, sortable: true, truncate: true, sortValue: sortValues.owner, render: (drawing) => <PersonChip name={drawing.resp} /> },
    {
      key: 'since', header: 'Blocked for', width: 104, sortable: true, defaultSortDir: 'desc', sortValue: sortValues.since,
      render: (drawing) => {
        const text = blockedSinceText(since.get(drawing.id));
        if (!text) return <Dash />;
        // 14 #6: an age counted from the Excel import (no status history) says so: "since import".
        return (
          <span title={text.title} data-testid={text.fallback ? 'blocked-since-fallback' : undefined}>
            <AgeCell days={since.get(drawing.id).days} sub={text.sub} />
          </span>
        );
      },
    },
    {
      key: 'actions', header: '', width: 128, hideable: false, cellClassName: 'pl-1',
      render: (drawing) => (
        <div className="flex items-center justify-end gap-0.5">
          {readOnly ? null : <UnblockMenu onPick={(status) => onSetStatus(drawing, status)} testId={`unblock-${drawing.id}`} className="gap-1 px-2" />}
          <RowActions label={`More actions for ${drawingTitle(drawing)}`} items={menuFor(drawing)} size="sm" />
        </div>
      ),
    },
  ], [ambiguousTitles, applySuggestion, drawings, menuFor, onSaveReason, onSetStatus, people, readOnly, since, sortValues]);

  const [sortKey, sortDir] = sortValue.split(':');
  const sort = { key: sortKey, dir: sortDir };
  const sortedRows = useMemo(() => sortWithColumns(rows, sort, columns), [rows, sortValue, columns]); // eslint-disable-line react-hooks/exhaustive-deps
  const groupBy = useCallback((drawing) => ({ key: projectKeyOf(drawing), label: projects.get(projectKeyOf(drawing))?.label || drawing.project }), [projects]);
  const maxDays = useCallback((group) => Math.max(-1, ...group.rows.map((drawing) => daysOf(drawing) ?? -1)), [daysOf]);
  const sortGroups = useMemo(() => (sortKey === 'since' ? blockedGroupSort(daysOf, sortDir) : undefined), [sortKey, sortDir, daysOf]);
  const groups = useMemo(() => orderedGroups(sortedRows, groupBy, sortGroups), [sortedRows, groupBy, sortGroups]);

  const viewKey = JSON.stringify([filters, sortValue]);
  const [collapsedState, setCollapsedState] = useState({ key: null, value: [] });
  const collapsed = useMemo(() => {
    if (collapsedState.key === viewKey) return collapsedState.value;
    if (anyFilter) return [];
    const open = new Set(defaultOpenOnPage(groups, { rowLimit: GROUP_ROW_LIMIT, budget: DEFAULT_OPEN_ROWS }));
    return groups.filter((group) => !open.has(group.key)).map((group) => group.key);
  }, [collapsedState, viewKey, anyFilter, groups]);
  const setCollapsed = useCallback((value) => setCollapsedState({ key: viewKey, value }), [viewKey]);

  const selectedRows = useMemo(() => {
    const set = new Set(selectedIds);
    return rows.filter((drawing) => set.has(drawing.id));
  }, [rows, selectedIds]);

  // A failure keeps the selection, so the person can try again.
  const bulkUnblock = async (status) => {
    if (!selectedRows.length) return;
    setBulkBusy(true);
    try {
      await onBulkStatus(selectedRows.map((drawing) => drawing.id), status);
      setSelectedIds([]);
    } catch (err) {
      unblockFailed(err);
    } finally {
      setBulkBusy(false);
    }
  };

  const sortColumn = columns.find((column) => column.key === sortKey);
  const sortLabel = SORTS.find((item) => item.value === sortValue)?.label || `${sortColumn?.title || sortColumn?.header || 'Blocked for'}${sortDir === 'desc' ? ' ↓' : ' ↑'}`;
  const sortedBy = { 'since:desc': 'sorted by longest blocked', 'since:asc': 'most recently blocked first', 'drawing:asc': 'sorted by drawing', 'owner:asc': 'sorted by owner' }[sortValue] || `sorted by ${String(sortLabel).toLowerCase()}`;

  const saysWhat = [
    mismatches.approved.length ? `“APPROVED” (${fmt(mismatches.approved.length)})` : '',
    mismatches.submitted.length ? `that it was submitted (${fmt(mismatches.submitted.length)})` : '',
  ].filter(Boolean).join(' or ');
  const staleReasons = [...new Set(mismatches.stale.map((drawing) => labelize(drawing.blocker, BLOCKER_LABELS)))].slice(0, 2).join(', ')
    + (new Set(mismatches.stale.map((drawing) => drawing.blocker)).size > 2 ? '…' : '');
  const mismatchLines = [
    notReallyBlocked ? (
      <span key="not-blocked" className="block">
        {fmt(notReallyBlocked)} of the {fmt(blocked.length)} may not be blocked at all: {notReallyBlocked === 1 ? 'its' : 'their'} Action says {saysWhat}.
      </span>
    ) : null,
    mismatches.code_c.length ? <span key="code-c" className="block">{fmt(mismatches.code_c.length)} without a reason say “CODE C” — the consultant’s comments are the reason.</span> : null,
    mismatches.stale.length ? <span key="stale" className="block">{fmt(mismatches.stale.length)} approved or completed {mismatches.stale.length === 1 ? 'drawing still carries' : 'drawings still carry'} a blocker ({staleReasons}).</span> : null,
  ].filter(Boolean);
  const showCallout = !loading && (missing > 0 || mismatchTotal > 0);
  // 14 #6: say when ages count from the import (or the day a drawing was added) instead of a status change.
  const fallbackNote = useMemo(() => blockedFallbackNote([...since.values()]), [since]);

  return (
    <div className="space-y-3" data-testid="blocked-tab">
      {showCallout ? (
        <Callout
          tone="amber"
          icon={CircleAlert}
          testId="blocked-reason-callout"
          title={<b className="font-bold">{missing ? `${fmt(missing)} of ${fmt(blocked.length)} blocked drawings have no reason recorded` : 'Some action notes disagree with the status'}</b>}
          sub={mismatchLines.length
            ? <span data-testid="blocked-mismatch-lines">{mismatchLines}</span>
            : `Without a reason nobody knows who has to act.${readOnly ? '' : ' Click a “Reason missing” badge, or set one reason for many drawings at once.'}`}
          actions={(
            <>
              {missing && !readOnly ? (
                <Button type="button" size="sm" variant="outline" onClick={() => { setReasonsPreselect(null); setReasonsOpen(true); }} data-testid="set-blocked-reasons-btn">
                  Set reasons ({fmt(missing)})
                </Button>
              ) : null}
              {mismatchTotal && canApply ? (
                <Button type="button" size="sm" variant="ghost" onClick={() => setMismatchOpen(true)} data-testid="review-mismatches-btn">
                  Review {fmt(mismatchTotal)} {mismatchTotal === 1 ? 'mismatch' : 'mismatches'}
                </Button>
              ) : null}
            </>
          )}
        />
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter by reason">
          <ReasonChip active={!filters.reason} onClick={() => set({ reason: null })} label="All reasons" count={blocked.length} testId="blocked-reason-all" />
          {reasons.map((reason) => (
            <ReasonChip
              key={reason.key}
              active={filters.reason === reason.key}
              onClick={() => set({ reason: filters.reason === reason.key ? null : reason.key })}
              label={reason.label}
              count={reason.count}
              tone={reason.tone}
              testId={`blocked-reason-${reason.key.toLowerCase()}`}
            />
          ))}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" size="sm" className="text-ink-2" data-testid="blocked-sort">
                <ArrowUpDown aria-hidden="true" /> Sort: <b className="font-bold text-ink">{sortLabel}</b>
                <ChevronDown className="!size-3.5 text-ink-3" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[210px] rounded-xl border-line bg-surface p-1.5 text-ink shadow-overlay">
              <DropdownMenuRadioGroup value={sortValue} onValueChange={setSortValue}>
                {SORTS.map((item) => <DropdownMenuRadioItem key={item.value} value={item.value} className="h-[34px] rounded-lg text-[13px]">{item.label}</DropdownMenuRadioItem>)}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
          <span className="hidden h-[18px] w-px bg-line md:block" aria-hidden="true" />
          <DigestButton digest={digest} onClick={() => { setDigestOpen(true); if (!digest.loading) digest.reload(); }} />
          {onViewChange ? <ViewSwitch value={view} onChange={onViewChange} options={BLOCKED_VIEWS} ariaLabel="Show blocked drawings as" testId="blocked-view" /> : null}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <SearchField
          value={filters.search}
          onChange={(value) => set({ search: value })}
          placeholder="Search blocked drawings…"
          label="Search blocked drawings"
          testId="blocked-search"
          className="w-full sm:w-[260px]"
        />
        <ChipSelect label="Project" value={filters.project} options={options.project} onChange={(value) => set({ project: value })} testId="blocked-filter-project" />
        <ChipSelect label="Discipline" value={filters.disc} options={options.disc} onChange={(value) => set({ disc: value })} testId="blocked-filter-discipline" />
        <ChipSelect label="Owner" value={filters.owner} options={options.owner} onChange={(value) => set({ owner: value })} testId="blocked-filter-owner" />
        {anyFilter ? (
          <span className="ml-auto whitespace-nowrap text-[12.5px] text-ink-3" data-testid="blocked-count" aria-live="polite">
            <b className="font-semibold text-ink-2">{fmt(rows.length)}</b> of {fmt(blocked.length)} blocked
          </span>
        ) : null}
      </div>

      {board ? (
        <BlockedBoard
          rows={sortedRows}
          onOpen={onOpen}
          onSetReason={readOnly ? undefined : (drawing, blocker) => onSaveReason({ ids: [drawing.id], blocker, note: '' })}
          onUnblock={readOnly ? undefined : (drawing) => onSetStatus(drawing, 'IN_PROGRESS')}
        />
      ) : (
        <DataTable
          testId="blocked-table"
          ariaLabel="Blocked drawings"
          columns={columns}
          rows={sortedRows}
          rowKey="id"
          loading={loading}
          skeletonRows={8}
          sort={sort}
          onSortChange={(next) => setSortValue(next ? `${next.key}:${next.dir}` : 'since:desc')}
          manualSort
          selectable={!readOnly}
          selectedKeys={selectedIds}
          onSelectionChange={setSelectedIds}
          getRowLabel={drawingTitle}
          onRowOpen={onOpen}
          rowTestId={(drawing) => `blocked-card-${drawing.id}`}
          groupBy={groupBy}
          sortGroups={sortGroups}
          collapsedGroups={collapsed}
          onCollapsedGroupsChange={setCollapsed}
          // Kit group row (14 mockup): "ATS Majlis SAMHA · 15 blocked · since 6 Jul 2026", 40px.
          groupRowSize="lg"
          groupCount={() => null}
          renderGroupHeader={(group) => {
            const facts = projects.get(group.key);
            // The oldest "since" of the group; on the same day a real date to Blocked wins over the import fallback.
            const oldest = group.rows.map((drawing) => since.get(drawing.id)).filter(Boolean)
              .sort((a, b) => (a.iso < b.iso ? -1 : a.iso > b.iso ? 1 : Number(a.source === 'created') - Number(b.source === 'created')))[0];
            return (
              <GroupTitle
                label={facts?.label || group.label}
                number={facts?.number}
                noNumberText="no project no."
                discs={disciplinesOf(group.rows)}
                count={`· ${fmt(group.rows.length)} blocked`}
                meta={oldest ? `· ${blockedSinceText(oldest).group}` : null}
              />
            );
          }}
          groupMeta={(group) => {
            const days = maxDays(group);
            return days >= 0 ? <AgeText days={days} className="text-[12.5px] font-bold" /> : null;
          }}
          groupRowLimit={GROUP_ROW_LIMIT}
          groupMoreVariant="compact"
          pagingKey={viewKey}
          footer={(
            <TableFoot
              className="rounded-b-card"
              testId="blocked-footer"
              left={(
                <span>
                  {rows.length
                    ? <><b className="font-semibold text-ink-2">{fmt(rows.length)}</b> blocked in <b className="font-semibold text-ink-2">{fmt(groups.length)}</b> {groups.length === 1 ? 'project' : 'projects'} · <b className="font-semibold text-ink-2">{fmt(rows.filter((drawing) => blockerMeta(drawing).missing).length)}</b> without a reason · {sortedBy}{fallbackNote ? <span data-testid="blocked-footer-fallback"> · {fallbackNote}</span> : null}</>
                    : 'No blocked drawings'}
                </span>
              )}
            >
              <Button type="button" variant="ghost" size="sm" onClick={onExport} disabled={!rows.length} data-testid="export-blocked-view-btn">
                <Download aria-hidden="true" /> Export this view
              </Button>
            </TableFoot>
          )}
          empty={(
            <EmptyState
              icon={SearchX}
              titleAs="p"
              title={blocked.length ? 'No blocked drawings match these filters' : 'Nothing is blocked'}
              text={blocked.length ? 'Try another reason or clear the filters.' : 'Drawings set to Blocked show up here with their reason and owner.'}
              action={anyFilter ? <Button type="button" variant="outline" size="sm" onClick={() => onFiltersChange({ reason: null, project: null, disc: null, owner: null, search: '' })}>Clear filters</Button> : null}
            />
          )}
          renderBulkBar={readOnly ? undefined : ({ count, clear }) => (
            <BulkBar count={count} onClear={clear} testId="blocked-bulkbar" label={`blocked ${count === 1 ? 'drawing' : 'drawings'} selected`}>
              <BulkButton
                icon={CircleAlert}
                onClick={() => { setReasonsPreselect(selectedRows.map((drawing) => drawing.id)); setReasonsOpen(true); }}
                disabled={bulkBusy}
                data-testid="blocked-bulk-reason"
              >
                Set reason…
              </BulkButton>
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <BulkButton icon={bulkBusy ? Loader2 : Unlock} disabled={bulkBusy} data-testid="blocked-bulk-unblock">
                    Unblock <ChevronDown className="h-3 w-3 opacity-80" aria-hidden="true" />
                  </BulkButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" side="top" className="min-w-[200px] rounded-xl border-line bg-surface p-1.5 text-ink shadow-overlay">
                  <DropdownMenuLabel className="px-2.5 pb-1 pt-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Move {fmt(count)} to</DropdownMenuLabel>
                  {UNBLOCK_STATUSES.map((status) => (
                    <DropdownMenuItem key={status} className={MENU_ITEM} onSelect={() => bulkUnblock(status)} data-testid={`blocked-bulk-unblock-${status}`}>
                      <span className={cn('ui-tone-dot', `tone-${getStatusTone(status, 'shop_drawing')}`)} style={{ width: 8, height: 8 }} aria-hidden="true" />
                      {labelize(status, STATUS_LABELS)}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              {canRequest ? (
                <BulkButton icon={Mail} onClick={() => requestInfo(selectedRows)} disabled={bulkBusy} data-testid="blocked-bulk-request-info">
                  Request info
                </BulkButton>
              ) : null}
            </BulkBar>
          )}
        />
      )}

      <DigestPanel
        open={digestOpen}
        onOpenChange={setDigestOpen}
        digest={digest}
        onShowOwner={(owner) => { setDigestOpen(false); onFiltersChange({ ...filters, owner, reason: null }); }}
        readOnly={readOnly}
      />
      {canApply ? (
        <MismatchDialog
          open={mismatchOpen}
          onOpenChange={setMismatchOpen}
          groups={mismatches}
          onOpenDrawing={onOpen}
          onApply={(changes) => onApplyChanges(changes, { purpose: 'suggestions', message: `${fmt(changes.length)} ${changes.length === 1 ? 'mismatch' : 'mismatches'} fixed` })}
        />
      ) : null}
      {canRequest ? (
        <EmailComposer
          open={Boolean(requestFor)}
          onOpenChange={(open) => { if (!open) setRequestFor(null); }}
          title={requestFor?.length === 1 ? `Request info · ${drawingTitle(requestFor[0])}` : `Request info · ${fmt(requestFor?.length || 0)} blocked drawings`}
          description="A ready email for the consultant, the architect or the site. Fill in what exactly is missing."
          initial={requestFor ? requestInfoEmail(requestFor, { sender: currentUserName }) : {}}
          summary={requestFor ? (
            <span>
              <b className="font-semibold text-ink">{fmt(requestFor.length)}</b> {requestFor.length === 1 ? 'drawing' : 'drawings'} · {[...new Set(requestFor.map((drawing) => drawing.project).filter(Boolean))].slice(0, 3).join(' · ') || 'no project'}
              {' · '}each one’s history records the request
            </span>
          ) : null}
          onSubmit={async (payload) => {
            await onRequestInfo({ ...payload, ids: requestFor.map((drawing) => drawing.id) });
            setSelectedIds([]);
          }}
          testId="request-info"
        />
      ) : null}

      {readOnly ? null : (
        <SetReasonsDialog
          open={reasonsOpen}
          onOpenChange={setReasonsOpen}
          drawings={reasonsPreselect ? blocked.filter((drawing) => reasonsPreselect.includes(drawing.id) || blockerMeta(drawing).missing) : blocked.filter((drawing) => blockerMeta(drawing).missing)}
          preselect={reasonsPreselect || (anyFilter ? rows.filter((drawing) => blockerMeta(drawing).missing).map((drawing) => drawing.id) : [])}
          onSave={async (ids, reason) => {
            await onSetReasons(ids, reason);
            setSelectedIds([]);
          }}
        />
      )}
    </div>
  );
}

function ReasonChip({ active, label, count, tone, onClick, testId }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'inline-flex h-[30px] items-center gap-1.5 whitespace-nowrap rounded-full border px-[11px] text-[12.5px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active
          ? 'border-[color:var(--brand-soft-border)] bg-brand-soft text-[color:var(--brand-ink)]'
          : 'border-line bg-surface text-ink-2 hover:bg-surface-hover hover:text-ink'
      )}
    >
      {tone ? <span className={cn('ui-tone-dot', `tone-${tone}`)} style={{ width: 7, height: 7 }} aria-hidden="true" /> : null}
      {label}
      <span className={cn('tabular-nums', active ? 'font-bold' : 'font-semibold text-ink-3')}>{fmt(count)}</span>
    </button>
  );
}

/**
 * Pick drawings (grouped by project) and one reason; saves through the existing bulk edit,
 * one undo step for the whole group.
 */
function SetReasonsDialog({ open, onOpenChange, drawings, preselect, onSave }) {
  const [reason, setReason] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const groups = useMemo(() => {
    const map = new Map();
    drawings.forEach((drawing) => {
      const key = String(drawing.project || 'No project').trim();
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(drawing);
    });
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [drawings]);

  // Reset the form every time the dialog opens (it is opened from the page, not by Radix).
  const [lastOpen, setLastOpen] = useState(false);
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) {
      setReason('');
      setError('');
      setSelected(new Set(preselect));
    }
  }

  const toggle = (ids, checked) => setSelected((current) => {
    const next = new Set(current);
    ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
    return next;
  });

  const save = async () => {
    if (!reason || !selected.size) return;
    setSaving(true);
    setError('');
    try {
      await onSave([...selected], reason);
      onOpenChange(false);
    } catch (err) {
      setError(err?.message || 'Could not save the reasons');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-hidden rounded-2xl border-line bg-surface p-0 text-ink">
        <div className="flex max-h-[88vh] flex-col">
          <DialogHeader className="border-b border-line-2 px-5 pb-3 pt-5 text-left">
            <DialogTitle className="font-heading text-[17px] font-bold text-ink">Set blocker reasons</DialogTitle>
            <DialogDescription className="text-[13px] text-ink-3">
              Tick the drawings, pick one reason, save. Each drawing keeps the change in its history.
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            <fieldset>
              <legend className="mb-2 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Reason</legend>
              <div className="grid gap-1.5 sm:grid-cols-2" role="radiogroup" aria-label="Blocker reason">
                {BLOCKER_REASONS.map((value) => (
                  <label
                    key={value}
                    className={cn(
                      'flex h-9 cursor-pointer items-center gap-2.5 rounded-lg border px-3 text-[13px] transition-colors',
                      reason === value ? 'border-[color:var(--brand-soft-border)] bg-brand-soft font-semibold text-ink' : 'border-line hover:bg-surface-hover'
                    )}
                  >
                    <input
                      type="radio"
                      name="blocker-reason"
                      value={value}
                      checked={reason === value}
                      onChange={() => setReason(value)}
                      className="h-4 w-4 accent-[color:var(--brand)]"
                      data-testid={`reason-option-${value}`}
                    />
                    {labelize(value, BLOCKER_LABELS)}
                    {BLOCKER_HINTS[value] ? <span className="font-normal text-ink-3">({BLOCKER_HINTS[value]})</span> : null}
                  </label>
                ))}
              </div>
            </fieldset>

            <div className="mt-5 flex items-center justify-between gap-3">
              <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Drawings</p>
              <button
                type="button"
                className="text-[12.5px] font-semibold text-brand-ink hover:underline"
                onClick={() => toggle(drawings.map((drawing) => drawing.id), selected.size !== drawings.length)}
              >
                {selected.size === drawings.length ? 'Clear all' : `Select all ${fmt(drawings.length)}`}
              </button>
            </div>
            <div className="mt-2 divide-y divide-line-2 overflow-hidden rounded-xl border border-line">
              {groups.map(([project, items]) => {
                const ids = items.map((drawing) => drawing.id);
                const count = ids.filter((id) => selected.has(id)).length;
                return (
                  <div key={project}>
                    <label className="flex cursor-pointer items-center gap-2.5 bg-surface-2 px-3 py-2 text-[13px] font-semibold text-ink">
                      <SdCheckbox
                        checked={count === ids.length}
                        indeterminate={count > 0 && count < ids.length}
                        onCheckedChange={(checked) => toggle(ids, Boolean(checked))}
                        aria-label={`Select all ${items.length} in ${project}`}
                        data-testid={`reason-group-${project}`}
                      />
                      <span className="min-w-0 flex-1 truncate">{project}</span>
                      <CountPill tone={count ? 'navy' : 'gray'}>{count ? `${count} of ${items.length}` : items.length}</CountPill>
                    </label>
                    <ul>
                      {items.map((drawing) => (
                        <li key={drawing.id}>
                          <label className="flex cursor-pointer items-center gap-2.5 px-3 py-1.5 pl-9 text-[12.5px] hover:bg-surface-hover">
                            <SdCheckbox
                              checked={selected.has(drawing.id)}
                              onCheckedChange={(checked) => toggle([drawing.id], Boolean(checked))}
                              aria-label={`Select ${drawingTitle(drawing)}`}
                            />
                            <span className="min-w-0 flex-1 truncate text-ink">{drawingTitle(drawing)}</span>
                            <span className="shrink-0 truncate text-ink-3">{[drawing.block, drawing.floor].filter(Boolean).join(' · ')}</span>
                            {!blockerMeta(drawing).missing ? <ReasonBadge drawing={drawing} /> : null}
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line-2 px-5 py-3">
            <p className="text-[12.5px] text-ink-3" role={error ? 'alert' : undefined}>
              {error ? <span className="font-semibold text-status-red-ink">{error}</span> : `${fmt(selected.size)} selected`}
            </p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
              <Button type="button" onClick={save} disabled={!reason || !selected.size || saving} data-testid="save-blocked-reasons-btn">
                {saving ? 'Saving…' : selected.size ? `Set reason for ${fmt(selected.size)}` : 'Set reason'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

